import React, { useState } from 'react';
import { Article } from '../../types.ts';
import { BookOpen, Check, Link, Plus, Search, Trash2, ExternalLink } from 'lucide-react';

interface ProductRelatedArticlesManagerProps {
  allArticles: Article[];
  selectedArticleIds: string[];
  onChangeArticleIds: (ids: string[]) => void;
  customLinks?: { title: string; url: string }[];
  onChangeCustomLinks?: (links: { title: string; url: string }[]) => void;
  primaryColor?: string;
}

export default function ProductRelatedArticlesManager({
  allArticles,
  selectedArticleIds = [],
  onChangeArticleIds,
  customLinks = [],
  onChangeCustomLinks,
  primaryColor = '#059669',
}: ProductRelatedArticlesManagerProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddCustom, setShowAddCustom] = useState(false);
  const [customTitle, setCustomTitle] = useState('');
  const [customUrl, setCustomUrl] = useState('');

  const filteredArticles = allArticles.filter((art) => {
    const q = searchTerm.toLowerCase();
    return (
      art.title.toLowerCase().includes(q) ||
      (art.categoryName && art.categoryName.toLowerCase().includes(q)) ||
      art.summary?.toLowerCase().includes(q)
    );
  });

  const handleToggleArticle = (idOrSlug: string) => {
    if (selectedArticleIds.includes(idOrSlug)) {
      onChangeArticleIds(selectedArticleIds.filter((id) => id !== idOrSlug));
    } else {
      onChangeArticleIds([...selectedArticleIds, idOrSlug]);
    }
  };

  const handleRemoveArticle = (idOrSlug: string) => {
    onChangeArticleIds(selectedArticleIds.filter((id) => id !== idOrSlug));
  };

  const handleAddCustomLink = (e: React.FormEvent) => {
    e.preventDefault();
    if (!customTitle.trim() || !customUrl.trim()) return;
    if (onChangeCustomLinks) {
      onChangeCustomLinks([
        ...customLinks,
        { title: customTitle.trim(), url: customUrl.trim() },
      ]);
    }
    setCustomTitle('');
    setCustomUrl('');
    setShowAddCustom(false);
  };

  const handleRemoveCustomLink = (index: number) => {
    if (onChangeCustomLinks) {
      onChangeCustomLinks(customLinks.filter((_, idx) => idx !== index));
    }
  };

  const selectedArticleObjects = allArticles.filter(
    (art) => selectedArticleIds.includes(art.id) || selectedArticleIds.includes(art.slug)
  );

  return (
    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200 space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 border-b border-slate-200/80 pb-3">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-emerald-700" />
          <h4 className="text-xs font-bold uppercase tracking-wider text-slate-800">
            Artikel Blog Pelengkap Informasi (Opsional)
          </h4>
        </div>
        <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 w-fit">
          {selectedArticleIds.length + customLinks.length} Artikel Terhubung
        </span>
      </div>

      <p className="text-xs text-slate-500 leading-relaxed">
        Pilih satu atau lebih artikel blog/wawasan yang sudah dibuat di website untuk ditampilkan di halaman detail produk ini sebagai referensi, rundown, tips panduan, atau ulasan kegiatan.
      </p>

      {/* Selected Articles Pills / Badges */}
      {(selectedArticleObjects.length > 0 || customLinks.length > 0) && (
        <div className="space-y-2 p-3 bg-white rounded-xl border border-slate-200">
          <span className="text-[10px] font-bold uppercase text-slate-400 block tracking-wider">
            Artikel yang Terlampir pada Produk Ini:
          </span>
          <div className="flex flex-wrap gap-2">
            {selectedArticleObjects.map((art) => (
              <div
                key={art.id}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-900 border border-emerald-200 text-xs font-medium"
              >
                <BookOpen className="w-3 h-3 text-emerald-600 shrink-0" />
                <span className="max-w-[200px] truncate">{art.title}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveArticle(art.id)}
                  className="p-0.5 text-emerald-600 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors ml-1"
                  title="Hapus kaitan artikel"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}

            {customLinks.map((link, idx) => (
              <div
                key={idx}
                className="flex items-center gap-1.5 px-2.5 py-1 rounded-lg bg-blue-50 text-blue-900 border border-blue-200 text-xs font-medium"
              >
                <ExternalLink className="w-3 h-3 text-blue-600 shrink-0" />
                <span className="max-w-[180px] truncate">{link.title}</span>
                <button
                  type="button"
                  onClick={() => handleRemoveCustomLink(idx)}
                  className="p-0.5 text-blue-600 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors ml-1"
                  title="Hapus tautan kustom"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Article Search & Selection List */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Cari judul artikel atau topik blog..."
            className="w-full pl-8 pr-3 py-1.5 text-xs bg-white border border-slate-200 rounded-xl focus:ring-1 focus:ring-emerald-500 focus:outline-hidden"
          />
        </div>

        <div className="max-h-48 overflow-y-auto space-y-1 pr-1 scrollbar-thin">
          {filteredArticles.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-4 italic">
              Tidak ada artikel yang cocok dengan pencarian.
            </p>
          ) : (
            filteredArticles.map((art) => {
              const isSelected =
                selectedArticleIds.includes(art.id) || selectedArticleIds.includes(art.slug);
              return (
                <div
                  key={art.id}
                  onClick={() => handleToggleArticle(art.id)}
                  className={`p-2 rounded-xl border flex items-center justify-between gap-3 cursor-pointer transition-all ${
                    isSelected
                      ? 'bg-emerald-50/80 border-emerald-300 text-emerald-950'
                      : 'bg-white border-slate-200 hover:border-slate-300 text-slate-700'
                  }`}
                >
                  <div className="flex items-center gap-2.5 min-w-0">
                    <img
                      src={art.coverImage || 'https://images.unsplash.com/photo-1506744038136-46273834b3fb?auto=format&fit=crop&w=150&q=80'}
                      alt=""
                      className="w-9 h-9 rounded-lg object-cover shrink-0 bg-slate-100"
                    />
                    <div className="min-w-0">
                      <h5 className="text-xs font-bold truncate">{art.title}</h5>
                      <span className="text-[10px] text-slate-500">
                        Kategori: {art.categoryName || 'Berita'} • /artikel/{art.slug}
                      </span>
                    </div>
                  </div>

                  <div
                    className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 transition-colors ${
                      isSelected
                        ? 'bg-emerald-600 border-emerald-600 text-white'
                        : 'border-slate-300 bg-white'
                    }`}
                  >
                    {isSelected && <Check className="w-3.5 h-3.5 stroke-[3]" />}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Option to Add Custom External/Internal Article URL Link */}
      <div className="pt-2 border-t border-slate-200">
        {!showAddCustom ? (
          <button
            type="button"
            onClick={() => setShowAddCustom(true)}
            className="text-xs font-bold text-slate-600 hover:text-emerald-700 flex items-center gap-1.5 transition-colors"
          >
            <Plus className="w-3.5 h-3.5 text-emerald-600" />
            <span>+ Tambahkan Link Artikel / Dokumen Khusus Manual (URL)</span>
          </button>
        ) : (
          <div className="p-3 bg-white rounded-xl border border-slate-200 space-y-2.5">
            <h5 className="text-xs font-bold text-slate-800 flex items-center gap-1">
              <Link className="w-3.5 h-3.5 text-blue-600" />
              <span>Tambah Tautan Artikel Manual:</span>
            </h5>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <input
                type="text"
                placeholder="Judul Artikel / Dokumen (contoh: Rundown PDF Outbound)"
                value={customTitle}
                onChange={(e) => setCustomTitle(e.target.value)}
                className="px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg"
              />
              <input
                type="url"
                placeholder="https://example.com/artikel-rundown atau /artikel/slug"
                value={customUrl}
                onChange={(e) => setCustomUrl(e.target.value)}
                className="px-3 py-1.5 text-xs bg-slate-50 border border-slate-200 rounded-lg font-mono text-slate-700"
              />
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <button
                type="button"
                onClick={() => {
                  setShowAddCustom(false);
                  setCustomTitle('');
                  setCustomUrl('');
                }}
                className="px-3 py-1 text-xs text-slate-500 font-bold"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleAddCustomLink}
                className="px-3 py-1 rounded-lg text-white text-xs font-bold shadow-xs"
                style={{ backgroundColor: primaryColor }}
              >
                Tambah Tautan
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
